// ============================================================================
// LES MISES EN FORME DES STATISTIQUES DE VENTES — un montant, un taux, une évolution, un coef, tels que le Résumé et le
// Reporting les affichent.
//
// ⚠️ UNE SEULE ÉCRITURE POUR LES ONGLETS : un même taux écrit « 20,7 % » ici et « 20.7% » à côté se lirait comme deux
//    nombres différents.
// ⚠️ UN INDICATEUR NON MESURABLE S'ÉCRIT « — », JAMAIS ZÉRO.
// ============================================================================
import { formatMoney, toInputValue } from '@orgazone/money-format';
import { rescale } from '@orgazone/core-money';
import { nombre, t } from '../i18n';

/** Des points de base en pourcentage (2070 → 20,7) — un TAUX, jamais une échelle d'argent. */
export const pctOf = (bp: number): number => bp / 100;

/** Un montant en unités entières de la devise (« 34 213 € ») ; « — » quand il n'est pas mesurable. */
export const eur0 = (cents: number | null): string => (cents === null ? '—' : formatMoney(cents, { displayScale: 0 }));

/** Un montant au centime (« 1,50 € ») — la cagnotte se compte en petites sommes qu'un arrondi à l'unité effacerait. */
export const fullMoney = (cents: number | null): string => (cents === null ? '—' : formatMoney(cents));

/** Un taux en points de base, à une décimale (« 20,7 % ») ; « — » quand il n'est pas mesurable. */
export const pc = (bp: number | null): string => (bp === null ? '—' : `${nombre(pctOf(bp), { fractionDigits: 1 })} %`);

/** Une évolution signée (« +2,5 % », « −11,1 % ») ; « — » quand elle ne se mesure pas. */
export const dTx = (bp: number | null): string => {
  if (bp === null) return '—';
  const sign = bp > 0 ? '+' : bp < 0 ? '−' : '';
  return `${sign}${nombre(pctOf(Math.abs(bp)), { fractionDigits: 1 })} %`;
};

/** Un coefficient × 10 000, à deux décimales (« ×2,33 ») ; « — » quand il n'est pas mesurable. */
export const coef = (bp: number | null): string => (bp === null ? '—' : `×${nombre(bp / 10000, { fractionDigits: 2 })}`);

/** Une quantité — entière en général, au millième pour une vente au poids. */
export const qty = (n: number | null): string => (n === null ? '—' : nombre(n, { fractionDigits: Number.isInteger(n) ? 0 : 3 }));

/**
 * LA VALEUR D'UNE FICHE SANS CE CHAMP, telle que le serveur la rend (`UNSET`, `sales/stats-summary.ts`) — un marqueur de
 * filtre et de regroupement, que l'écran NOMME dans la langue du compte.
 * ⚠️ Il voyage tel quel, jamais traduit : traduit, il cesserait de désigner les lignes qu'il regroupe.
 */
export const UNSET_VALUE = '∅';

/** Ce qu'on affiche d'une valeur de regroupement : elle-même, ou « non renseigné » dans la langue du compte. */
export const shownValue = (v: string): string => (v === UNSET_VALUE ? t('(non renseigné)') : v);

// ─── Les nombres d'un fichier exporté ────────────────────────────────────────────────────────────
// ⚠️ CE QUI SORT DE LA MAISON SUIT UNE CONVENTION FIXE, jamais la langue du compte : virgule décimale, aucun séparateur
//    de milliers (Excel FR, le format du fichier). Un « 20.7 » d'un compte anglais à côté d'un « 1234,56 » se lirait
//    comme du texte, et un séparateur de milliers ferait d'une cellule une chaîne.

/** Un taux en points de base, en pourcentage à deux décimales (2070 → « 20,70 ») ; vide s'il n'est pas mesurable. */
export const csvRate = (bp: number | null): string => (bp === null ? '' : toInputValue(bp, 2));

/** Un coefficient × 10 000, à deux décimales (23 333 → « 2,33 ») ; vide s'il n'est pas mesurable. */
export const csvCoef = (bp: number | null): string => (bp === null ? '' : toInputValue(rescale(bp, 4, 2), 2));

/** Une quantité : entière telle quelle, au poids en millièmes (0,25 → « 0,250 ») ; vide si elle n'est pas mesurable. */
export const csvQty = (q: number | null): string =>
  (q === null ? '' : Number.isInteger(q) ? String(q) : toInputValue(Math.round(q * 1000), 3));
